import React, { useMemo, useState } from 'react'
import { connect } from 'react-redux'
import debounce from 'lodash/debounce'

import { updateFilters } from '../../../actions/searchActions'
import CrossOutIcon from '../../../components/icons/CrossOut'

import CommonMultiSelectFilter from '../common/MultiSelectFilter'

import { FilterPanel, CloseButton, PanelHeader } from './shared'

import FilterPanelActions from './FilterPanelActions'

type FilterKey = keyof GQL_SearchFilters

interface MultiSelectFilterPanelProps {
  filterKey: FilterKey
  possibleFilters:
    | GQL_Region[]
    | GQL_AdventureType[]
    | Array<{
        slug: string
        name: string
      }>
  selectedFiltersSlugs: Array<string>
  onClose: (...args: any) => any
  update: (...args: any) => any
  heading: string
  open?: boolean
  noTruncate?: boolean
  resultsCount?: number
  updateDebounceMs?: number
}

const MultiSelectFilter = ({
  filterKey,
  possibleFilters = [],
  selectedFiltersSlugs: initialSelectedFiltersSlugs = [],
  onClose,
  update,
  heading,
  open,
  noTruncate,
  resultsCount,
  updateDebounceMs = 500,
}: MultiSelectFilterPanelProps) => {
  const [showResultCount, setShowResultCount] = useState(true)
  const debouncedUpdate = useMemo(
    () =>
      debounce((nextFilters: GQL_SearchFilters) => {
        update(nextFilters)
        setShowResultCount(true)
      }, updateDebounceMs),
    [update]
  )

  const [selectedFiltersSlugs, setSelectedFiltersSlugs] = useState(
    initialSelectedFiltersSlugs
  )

  const handleClosePanel = () => {
    onClose()
  }

  const handleApply = () => {
    window.scrollTo(0, 0)
    handleClosePanel()
  }

  const handleResetFilters = () => {
    update({ [filterKey]: null })
    setSelectedFiltersSlugs([])
    window.scrollTo(0, 0)
    onClose()
  }

  const handleSelectionChange = (nextFilters: GQL_SearchFilters) => {
    setSelectedFiltersSlugs(nextFilters[filterKey])
    setShowResultCount(false)
    debouncedUpdate(nextFilters)
  }

  return (
    <FilterPanel className={`${open ? 'show' : ''}`}>
      <PanelHeader>
        <span>{heading}</span>
        <CloseButton
          aria-label={`close ${filterKey}`}
          onClick={handleClosePanel}
        >
          <CrossOutIcon />
        </CloseButton>
      </PanelHeader>

      <CommonMultiSelectFilter
        name={filterKey}
        valueKey={filterKey}
        values={selectedFiltersSlugs}
        items={possibleFilters}
        onChange={handleSelectionChange}
        noTruncate={noTruncate}
      />

      <FilterPanelActions
        onClear={handleResetFilters}
        onView={handleApply}
        resultsCount={!showResultCount ? undefined : resultsCount}
      />
    </FilterPanel>
  )
}

export { MultiSelectFilter as MultiSelectFilterPanel }

export default connect(undefined, (dispatch) => ({
  // @ts-ignore -- updateFilters not typed yet
  update: (filters) => dispatch(updateFilters(filters)),
}))(MultiSelectFilter)
